import React, { useState, Fragment, useEffect, useMemo } from 'react';
import {
  ButtonGroup,
  Button,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getStatisticsSectionHeaderRowSx,
  getStatisticsTotalsRowSx,
  viewRootTextPrimarySx,
} from '../../util/chartTheme';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useStatistic from '../../hooks/useStatistic';
import useBranch from '../../hooks/useBranch';
import useEntertainmentType from '../../hooks/useEntertainmentType';

const dynamicAccess = (object, path) => {
  // Split the path into an array of level names
  const levels = path.split('.');

  // Initialize the result with the original object
  let result = object;

  // Iterate through the level names and access the sub-levels
  for (const level of levels) {
    if (result.hasOwnProperty(level)) {
      result = result[level];
    } else {
      // If the level does not exist, you can handle it in various ways:
      // You can throw an error, return a default value, etc.
      // Here, we're throwing an error:
      return '---';
    }
  }

  return result;
};

const tableOptionsPre = [
  {
    label: 'Erwarteter Gesamtumsatz',
    intName: 'regiondoStats.predictedTotal',
  },
  {
    label: 'Erwarteter Gesamtumsatz (nur Online-Zahlung)',
    intName: 'regiondoStats.predictedTotalOnline',
  },
  {
    label: 'Stattfindende Buchungen',
    intName: 'regiondoStats.bookingCount',
  },
  {
    label: 'Besucherzahlen',
    intName: 'regiondoStats.participantCount',
  },
  {
    label: 'Neu eingegangene Buchungen',
    intName: 'regiondoStats.addedBookingCount',
  },
  {
    label: 'Wert der neu eingegangenen Buchungen',
    intName: 'regiondoStats.addedBookingTotal',
  },
  {
    label: 'Durchschnittliche Vorausbuchung',
    intName: 'regiondoStats.leadTimeAverage',
  },
  {
    label: 'Anteil Ticketshopbuchungen',
    intName: 'regiondoStats.webstoreQuote',
  },
  {
    label: 'Anteil online bezahlt',
    intName: 'regiondoStats.paidOnlineQuote',
  },
  {
    label: 'Eingegangene Anrufe',
    intName: 'callStats.callsReceived',
  },
  {
    label: 'Eingegangene Anrufe (unique)',
    intName: 'callStats.callsReceivedUnique',
  },
  {
    label: 'Offene Rückrufe (Tagesende)',
    intName: 'callStats.callbacksOpen',
  },
  {
    label: 'Durchgeführte Rückrufe',
    intName: 'callStats.callbackCount',
  },
  {
    label: 'Direkt beantwortete Anrufe',
    intName: 'callStats.callsDirectlyAnswered',
  },
  {
    label: 'Anteil direkt beantwortete Anrufe',
    intName: 'callStats.callsDirectlyAnsweredQuote',
  },
  {
    label: 'Durchschnittliche Anrufdauer',
    intName: 'callStats.callsDurationAverage',
  },
  {
    label: 'Durchschnittliche Wartedauer',
    intName: 'callStats.callsWaittimeAverage',
  },
];

const Statistics = () => {
  const theme = useTheme();
  const totalsRowSx = useMemo(() => getStatisticsTotalsRowSx(theme), [theme]);
  const sectionHeaderRowSx = useMemo(() => getStatisticsSectionHeaderRowSx(theme), [theme]);

  const { getStatisticView, getStatisticDaterange } = useStatistic();
  const { getBranches, getAllEntertainmentsOfBranches } = useBranch();
  const { getEntertainmentTypes } = useEntertainmentType();

  // Zustandsvariablen
  const [interval, setInterval] = useState('day');
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [minDate, setMinDate] = useState(dayjs());
  const [maxDate, setMaxDate] = useState(dayjs());
  const [branch, setBranch] = useState([]);
  const [entertainment, setEntertainment] = useState([]);
  const [entertainmentType, setEntertainmentType] = useState([]);
  const [table, setTable] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [data, setData] = useState({ tables: [] });
  const [branchOptions, setBranchOptions] = useState([]);
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [entertainmentTypeOptions, setEntertainmentTypeOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState(tableOptionsPre);

  const fetchData = async () => {
    const startDateReq = startDate.format('YYYY-MM-DD');
    const endDateReq = endDate.format('YYYY-MM-DD');

    if (!branch.length || !entertainment.length || !table.length) return;

    const branchIds = branch.map(branch => branch.id);
    const entertainments = entertainment.map(entertainment => entertainment.label);
    const entertainmentTypeIds = entertainmentType.map(type => type.id);
    const tableIntNames = table.map(table => table.intName);

    try {
      const stats = await getStatisticView({
        startDate: startDateReq,
        endDate: endDateReq,
        branches: branchIds,
        entertainments: entertainments,
        entertainmentTypes: entertainmentTypeIds,
        tables: tableIntNames,
      });
      setData(stats);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistik');
      console.error('Error fetching statistics:', error);
    }
  };

  useEffect(() => {
    const f = async () => {
      // get branches for selection
      let branches = [];
      try {
        const branchResponse = await getBranches();
        branches = Array.isArray(branchResponse)
          ? branchResponse
          : Array.isArray(branchResponse?.branches)
            ? branchResponse.branches
            : [];
      } catch (error) {
        toast.error('Fehler beim Laden der Standorte');
        console.error('Error fetching branches:', error);
        return;
      }

      // get available daterange
      let daterange = null;
      try {
        daterange = await getStatisticDaterange();
      } catch (error) {
        toast.error('Fehler beim Laden des Datumsbereichs');
        console.error('Error fetching statistic date range:', error);
        setMinDate(startDate);
        setMaxDate(endDate);
        return;
      }

      let shownBranches = [];
      let branchIds = [];

      let mapPromise = branches.map(branch => {
        shownBranches.push({ label: branch.locationName, id: branch._id });
        branchIds.push(branch._id);
      });

      await Promise.all(mapPromise);

      setBranchOptions(shownBranches);
      setBranch(shownBranches);

      const minDayJS = daterange?.minDate ? dayjs(daterange.minDate) : startDate;
      const maxDayJS = daterange?.maxDate ? dayjs(daterange.maxDate) : endDate;

      setMinDate(minDayJS);
      setMaxDate(maxDayJS);
    };

    f();
  }, []);

  useEffect(() => {
    const f = async () => {
      let types = [];
      try {
        const typeResponse = await getEntertainmentTypes();
        types = Array.isArray(typeResponse) ? typeResponse : [];
      } catch (error) {
        toast.error('Fehler beim Laden der Entertainmenttypen');
        console.error('Error fetching entertainment types:', error);
        return;
      }

      const shownEntertainmentTypes = types
        .map(type => ({ label: type.name, id: type._id }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setEntertainmentTypeOptions(shownEntertainmentTypes);
    };

    f();
  }, []);

  useEffect(() => {
    if (!branch.length) return;

    const f = async () => {
      const branchIds = branch.map(branch => branch.id);

      let allEntertainments = [];
      try {
        const entertainmentResponse = await getAllEntertainmentsOfBranches(branchIds);
        allEntertainments = Array.isArray(entertainmentResponse)
          ? entertainmentResponse
          : Array.isArray(entertainmentResponse?.entertainments)
            ? entertainmentResponse.entertainments
            : [];
      } catch (error) {
        toast.error('Fehler beim Laden der Entertainments');
        console.error('Error fetching entertainments:', error);
        return;
      }

      const shownEntertainments = allEntertainments.map(entertainment => {
        return { label: entertainment };
      });

      setEntertainmentOptions(shownEntertainments);
      setEntertainment(shownEntertainments);
    };

    f();
  }, [branch]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, branch, entertainment, entertainmentType, table]);

  const statsTranslations = {
    'regiondoStats.predictedTotal': {
      desc: 'Erwarteter Gesamtumsatz',
      unit: '€',
      dotToComma: true,
      thousandSeparator: true,
    },
    'regiondoStats.predictedTotalOnline': {
      desc: 'Erwarteter Gesamtumsatz (nur Online-Zahlung)',
      unit: '€',
      dotToComma: true,
      thousandSeparator: true,
    },
    'regiondoStats.bookingCount': {
      desc: 'Stattfindende Buchungen',
      thousandSeparator: true,
    },
    'regiondoStats.participantCount': {
      desc: 'Besucherzahlen',
      thousandSeparator: true,
    },
    'regiondoStats.addedBookingCount': {
      desc: 'Neu eingegangene Buchungen',
      thousandSeparator: true,
    },
    'regiondoStats.addedBookingTotal': {
      desc: 'Wert der neu eingegangenen Buchungen',
      unit: '€',
      dotToComma: true,
      thousandSeparator: true,
    },
    'regiondoStats.leadTimeAverage': {
      desc: 'Durchschnittliche Vorausbuchung',
      unit: 'T',
      dotToComma: true,
    },
    'regiondoStats.webstoreQuote': {
      desc: 'Anteil Ticketshopbuchungen',
      unit: '%',
      dotToComma: true,
    },
    'regiondoStats.paidOnlineQuote': {
      desc: 'Anteil online bezahlt',
      unit: '%',
      dotToComma: true,
    },
    'callStats.callsReceived': {
      desc: 'Eingegangene Anrufe',
      thousandSeparator: true,
    },
    'callStats.callsReceivedUnique': {
      desc: 'Eingegangene Anrufe (unique)',
      thousandSeparator: true,
    },
    'callStats.callbacksOpen': {
      desc: 'Offene Rückrufe (Tagesende)',
      thousandSeparator: true,
    },
    'callStats.callbackCount': {
      desc: 'Durchgeführte Rückrufe',
      thousandSeparator: true,
    },
    'callStats.callsDirectlyAnswered': {
      desc: 'Direkt beantwortete Anrufe',
      thousandSeparator: true,
    },
    'callStats.callsDirectlyAnsweredQuote': {
      desc: 'Anteil direkt beantwortete Anrufe',
      unit: '%',
      dotToComma: true,
    },
    'callStats.callsDurationAverage': {
      desc: 'Durchschnittliche Anrufdauer',
      unit: 'sec',
      dotToComma: true,
    },
    'callStats.callsWaittimeAverage': {
      desc: 'Durchschnittliche Wartedauer',
      unit: 'sec',
      dotToComma: true,
    },
  };

  const valueHelper = (val, type) => {
    let newVal = val.toString();

    // Überprüfe, ob eine Einheit in den statsTranslations definiert ist und füge sie hinzu
    if (statsTranslations.hasOwnProperty(type) && statsTranslations[type].hasOwnProperty('unit')) {
      newVal += ' ' + statsTranslations[type].unit;
    }

    // Überprüfe, ob der Punkt durch ein Komma ersetzt werden soll
    if (statsTranslations.hasOwnProperty(type) && statsTranslations[type].dotToComma) {
      newVal = newVal.replace('.', ',');
    }

    // Überprüfe, ob 1000er-Punkte verwendet werden sollen
    if (statsTranslations.hasOwnProperty(type) && statsTranslations[type].thousandSeparator) {
      // Füge 1000er-Punkte hinzu, indem der Wert in eine Zahl umgewandelt und formatiert wird
      newVal = newVal.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return newVal;
  };

  // Event-Handler, um Sections auf- und zuzuklappen
  const handleToggleSection = (tableName, sectionIndex) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${tableName}-${sectionIndex}`]: !prev[`${tableName}-${sectionIndex}`],
    }));
  };

  // event handler for date changes
  const handleStartDateChanged = newDate => {
    if (newDate && newDate.isValid()) {
      setStartDate(newDate);
      if (endDate && newDate.isAfter(endDate)) setEndDate(newDate);
    }
  };

  const handleEndDateChanged = newDate => {
    if (newDate && newDate.isValid()) {
      setEndDate(newDate);
      if (startDate && newDate.isBefore(startDate)) setStartDate(newDate);
    }
  };

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        padding: 2,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
        {/* Filter-Abschnitt */}
        <Paper
          sx={{
            marginBottom: 2,
            padding: 2,
          }}
          elevation={3}
        >
          <Grid container rowSpacing={2} columnSpacing={2} alignItems='center'>
            <Grid sm={6} xs={12}>
              <Stack direction='row' spacing={2} alignItems='center'>
                {interval === 'day' && (
                  <>
                    <DatePicker
                      label='Startdatum'
                      value={startDate}
                      onChange={newValue => handleStartDateChanged(newValue)}
                      minDate={minDate}
                      maxDate={maxDate}
                    />

                    <DatePicker
                      label='Enddatum'
                      value={endDate}
                      onChange={newValue => handleEndDateChanged(newValue)}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </>
                )}
              </Stack>
            </Grid>
            {/* Für Woche und Monat: Ähnlicher Code, aber mit unterschiedlichen Auswahlelementen */}
            <Grid sm={6} xs={12}>
              <Stack direction='row' spacing={2} alignItems='center'>
                <Typography>Intervall:</Typography>
                <ButtonGroup variant='outlined' color='primary'>
                  <Button
                    onClick={() => setInterval('day')}
                    variant={interval === 'day' ? 'contained' : 'outlined'}
                  >
                    Tag
                  </Button>
                  {/*<Button
                    onClick={() => setInterval('week')}
                    variant={interval === 'week' ? 'contained' : 'outlined'}
                  >
                    Woche
                  </Button>
                  <Button
                    onClick={() => setInterval('month')}
                    variant={interval === 'month' ? 'contained' : 'outlined'}
                  >
                    Monat
                  </Button>*/}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid sm={4} xs={12}>
              {/* Branch-Auswahl */}
              <Autocomplete
                multiple
                id='branch'
                options={branchOptions}
                limitTags={3}
                getOptionKey={option => option.id}
                sx={{ width: '100%' }}
                value={branch}
                onChange={(_event, newValue) => {
                  setBranch(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={params => (
                  <TextField {...params} label='Branches' variant='outlined' />
                )}
              />
            </Grid>
            <Grid sm={4} xs={12}>
              {/* Entertainmenttyp-Auswahl */}
              <Autocomplete
                multiple
                id='entertainmentType'
                options={entertainmentTypeOptions}
                limitTags={3}
                getOptionKey={option => option.id}
                sx={{ width: '100%' }}
                value={entertainmentType}
                onChange={(_event, newValue) => {
                  setEntertainmentType(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={params => (
                  <TextField {...params} label='Entertainmenttypen' variant='outlined' />
                )}
              />
            </Grid>
            <Grid sm={4} xs={12}>
              {/* Entertainment-Auswahl */}
              <Autocomplete
                multiple
                id='entertainment'
                options={entertainmentOptions}
                limitTags={3}
                getOptionKey={option => option.label}
                sx={{ width: '100%' }}
                value={entertainment}
                onChange={(_event, newValue) => {
                  setEntertainment(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.label === value.label}
                renderInput={params => (
                  <TextField {...params} label='Entertainments' variant='outlined' />
                )}
              />
            </Grid>
            <Grid xs={12}>
              {/* Tabellen-Auswahl */}
              <Autocomplete
                multiple
                id='table'
                options={tableOptions}
                limitTags={2}
                getOptionKey={option => option.intName}
                sx={{ width: '100%' }}
                value={table}
                onChange={(_event, newValue) => {
                  setTable(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.intName === value.intName}
                renderInput={params => (
                  <TextField {...params} label='Tabellen' variant='outlined' />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tabellen-Abschnitt */}
        {data.tables.map((table, tableIndex) => (
          <Fragment key={tableIndex}>
            <Typography variant='h5'>
              {statsTranslations.hasOwnProperty(table) ? statsTranslations[table].desc : table}
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 5 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}></TableCell>
                    <TableCell>Standort / Entertainment</TableCell>
                    <TableCell>Summe</TableCell>
                    {data.headerData.map((heading, headingIndex) => (
                      <TableCell key={headingIndex}>
                        {dayjs(heading).format('ddd, DD.MM.')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data.map((section, sectionIndex) => (
                    <Fragment key={sectionIndex}>
                      <TableRow sx={sectionHeaderRowSx}>
                        <TableCell>
                          <IconButton
                            aria-label='expand row'
                            size='small'
                            onClick={() => handleToggleSection(table, sectionIndex)}
                          >
                            {expandedSections[`${table}-${sectionIndex}`] ? (
                              <KeyboardArrowUpIcon></KeyboardArrowUpIcon>
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{section.name}</TableCell>
                        <TableCell>{valueHelper(section.totals[table], table)}</TableCell>
                        {section.sums.map((sum, sumIndex) => (
                          <TableCell key={sumIndex}>{valueHelper(sum[table], table)}</TableCell>
                        ))}
                      </TableRow>

                      {section.entertainments.map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          style={{
                            display: expandedSections[`${table}-${sectionIndex}`] ? '' : 'none',
                          }}
                        >
                          <TableCell></TableCell>
                          <TableCell sx={{ pl: 3 }}>{row.name}</TableCell>
                          <TableCell sx={{ pl: 3 }}>
                            {valueHelper(row.sums[table], table)}
                          </TableCell>
                          {row.statsData.map((cell, cellIndex) => (
                            <TableCell key={cellIndex}>
                              {valueHelper(dynamicAccess(cell, table), table)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>

                {!statsTranslations[table].desc.toLowerCase().includes('durchschnittlich') && (
                  <TableBody>
                    <TableRow sx={totalsRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>Gesamt</TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        {valueHelper(
                          Number(
                            data.data
                              .reduce((sum, section) => sum + (section.totals[table] || 0), 0)
                              .toFixed(2)
                          ),
                          table
                        )}
                      </TableCell>
                      {data.headerData.map((_, headingIndex) => (
                        <TableCell key={headingIndex}>
                          {valueHelper(
                            Number(
                              data.data
                                .reduce(
                                  (sum, section) =>
                                    sum + (section.sums[headingIndex]?.[table] || 0),
                                  0
                                )
                                .toFixed(2)
                            ),
                            table
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Fragment>
        ))}
    </Box>
  );
};

export default Statistics;
